import { Button, Flex, Grid, Text } from '@approvalmax/ui/src/components';
import { selectors } from 'modules/common';
import { useSelector } from 'modules/react-redux';
import { FC, memo, useCallback, useMemo } from 'react';
import { useMount } from 'react-use';
import { amplitudeService } from 'services/amplitude';
import { routingService } from 'services/routing';

import { AdditionalResources } from '../AdditionalResources/AdditionalResources';
import { HeaderSection } from '../HeaderSection/HeaderSection';
import { IntroductionVideo } from '../IntroductionVideo/IntroductionVideo';
import { getTextByUseCase } from './GettingStarted.helpers';
import { useGettingStartedSteps } from './GettingStarted.hooks';
import { messages } from './GettingStarted.messages';
import { GettingStartedProps } from './GettingStarted.types';

export const GettingStarted: FC<GettingStartedProps> = memo((props) => {
    const { companyId, companyDisplayName, integrationType } = props;

    const profile = useSelector(selectors.profile.findProfile);
    const useCase = useMemo(() => getTextByUseCase(), []);

    const { stepsContent, isStandalone, completedSteps } = useGettingStartedSteps({
        ...props,
        useCase,
    });

    useMount(() => {
        amplitudeService.sendData('getting started: land on starterguide', {
            user_id: profile?.id,
            org_id: companyId,
            type: isStandalone ? 'GL' : 'Standalone',
        });
    });

    const handleSkip = useCallback(() => {
        amplitudeService.sendData('getting started: clicked on skip the guide', {
            user_id: profile?.id,
            org_id: companyId,
            type: isStandalone ? 'GL' : 'Standalone',
            completed_steps: completedSteps,
        });
        routingService.pushToDefaultPath();
    }, [companyId, completedSteps, isStandalone, profile?.id]);

    return (
        <Grid justifyContent='center'>
            <Grid maxWidth={944} width='100%' gap={32}>
                <HeaderSection useCase={useCase} companyDisplayName={companyDisplayName} />

                <Grid gap={16}>
                    <IntroductionVideo
                        companyId={companyId}
                        profileId={profile?.id}
                        integrationType={integrationType}
                    />

                    {stepsContent}
                </Grid>

                <Grid gap={16}>
                    <AdditionalResources />

                    <Flex spacing='2' justifyContent='center'>
                        <Text font='label' fontSize='xsmall' color='midnight80'>
                            {messages.skipGuide}
                        </Text>

                        <Button link size='xsmall' onClick={handleSkip}>
                            {messages.skipGuideButton}
                        </Button>
                    </Flex>
                </Grid>
            </Grid>
        </Grid>
    );
});

GettingStarted.displayName = 'GettingStarted';
